import { useEffect, useMemo, useState } from 'react';
import { accommodationsService } from '@/api/services';
import { usePublicCatalogData } from '@/components/PublicCatalogData';
import { getHotelImage, toImageSrc } from '@/utils/imageUrl';

const fallbackFeatures = [
  ['Seaside view', 'Family Suites', 'All Inclusive', 'Pool'],
  ['Adults Only', 'Spa', 'Fine Dining'],
  ['Private Beach', 'Kids Club', 'Water Sports'],
  ['Mountain View', 'Cycling Routes', 'Local Cuisine']
];

const formatErrorMessage = (error) => {
  if (!error) return 'Failed to load hotels. Please try again later.';
  if (typeof error === 'string') return error;
  if (error.response?.data?.message) return error.response.data.message;
  if (error.message) return error.message;
  return 'Failed to load hotels. Please try again later.';
};

const normalizeRegionSlug = (value) => {
  if (typeof value === 'string') {
    const v = value.trim();
    return v || null;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (value && typeof value === 'object') {
    const candidate = value.slug ?? value.value ?? value.code ?? value.name ?? null;
    if (typeof candidate === 'string') {
      const v = candidate.trim();
      return v || null;
    }
  }
  return null;
};

const buildImageSet = (_idx, images) => {
  if (!Array.isArray(images) || images.length === 0) return [];

  // Always resolve via getHotelImage so full URLs from API get replaceLocalhostUrl (media base) applied.
  // Some APIs may return image objects instead of raw strings.
  return images.map((img) => {
    const rawSrc = toImageSrc(img, null);
    if (!rawSrc) return null;
    if (rawSrc.startsWith('data:')) return rawSrc;
    return getHotelImage(rawSrc, null) || rawSrc;
  }).filter(Boolean);
};

const transformHotelRecord = (hotel, idx) => {
  // Prefer hotel.images (from API); fallback to hotel.media[].file_url for compatibility
  const rawImages = Array.isArray(hotel.images) && hotel.images.length > 0
    ? hotel.images
    : (Array.isArray(hotel.media) && hotel.media.length > 0
      ? hotel.media.map(m => m.file_url || m.url).filter(Boolean)
      : []);
  const imageSet = buildImageSet(idx, rawImages);
  const location =
    hotel.destination_name ||
    hotel.destination?.name ||
    hotel.region_name ||
    'Crete';

  const ratingValue = Number(hotel.rating);
  const starsValue = Number(hotel.stars);

  return {
    id: hotel.id,
    name: hotel.name || 'Hotel Name',
    location,
    images: imageSet,
    rating: Number.isFinite(ratingValue) ? ratingValue : 4 + (idx % 5) * 0.1,
    stars: Number.isInteger(starsValue) && starsValue >= 2 && starsValue <= 7 ? starsValue : null,
    price: hotel.price || hotel.min_price || 120 + idx * 15,
    features: Array.isArray(hotel.features) && hotel.features.length > 0
      ? hotel.features
      : fallbackFeatures[idx % fallbackFeatures.length],
    sale: hotel.sale ?? (idx % 3 === 0 ? '30% Off' : null),
    region:
      hotel.region_name || hotel.region?.name
        ? {
            id: hotel.region_id ?? hotel.region?.id,
            name: hotel.region_name || hotel.region?.name,
            slug: normalizeRegionSlug(hotel.region_slug ?? hotel.region?.slug)
          }
        : null,
    destination: hotel.destination_name
      ? {
          id: hotel.destination_id,
          name: hotel.destination_name,
          region_id: hotel.destination_region_id
        }
      : null,
    raw: hotel,
    // Pass through board types and offer (type + discount for same year as boards)
    boards: Array.isArray(hotel.boards) ? hotel.boards : [],
    board_type: hotel.board_type ?? null,
    offer: hotel.offer ?? null
  };
};

let cachedHotels = null;
let cachedError = null;
let cachedTimestamp = 0;
let inFlightPromise = null;

const fetchHotelsPayload = async (featuredOnly = false) => {
  // Featured hotels: use previous year (e.g. 2025 when current is 2026) for agreements and boards
  const dataYear = featuredOnly ? new Date().getFullYear() - 1 : null;
  const params = featuredOnly && dataYear != null
    ? { featured: 1, year: dataYear, boards_year: dataYear }
    : {};
  
  try {
    // Featured: only accommodations with agreements, boards from previous year
    // Other: getWithAgreements as before, with getAll fallback
    let response = await accommodationsService.getWithAgreements(params);
    let hotels = response.data?.data || response.data || [];
    
    if (!Array.isArray(hotels) || hotels.length === 0) {
      try {
        const fallback = await accommodationsService.getAll(params);
        hotels = fallback.data?.data || fallback.data || [];
      } catch {
        hotels = [];
      }
    }
    
    if (Array.isArray(hotels)) {
      hotels = hotels.filter(h => !(h.is_online === 0 || h.is_online === '0' || h.is_online === false));
    }
    if (featuredOnly && Array.isArray(hotels)) {
      hotels = hotels.filter(h => h.is_featured === 1 || h.is_featured === true || h.is_featured === '1');
    }
    
    return hotels;
  } catch (primaryError) {
    try {
      const fallback = await accommodationsService.getAll(params);
      let hotels = fallback.data?.data || fallback.data || [];
      if (Array.isArray(hotels)) {
        hotels = hotels.filter(h => !(h.is_online === 0 || h.is_online === '0' || h.is_online === false));
      }
      if (featuredOnly && Array.isArray(hotels)) {
        hotels = hotels.filter(h => h.is_featured === 1 || h.is_featured === true || h.is_featured === '1');
      }
      return hotels;
    } catch (secondaryError) {
      throw secondaryError || primaryError;
    }
  }
};

const requestHotels = async (featuredOnly = false) => {
  const payload = await fetchHotelsPayload(featuredOnly);
  if (!Array.isArray(payload)) {
    return [];
  }
  return payload.map(transformHotelRecord);
};

// Separate cache for featured hotels
let cachedFeaturedHotels = null;
let cachedFeaturedError = null;
let cachedFeaturedTimestamp = 0;
let inFlightFeaturedPromise = null;
const CACHE_TTL_MS = 2 * 60 * 1000;

const loadHotels = (featuredOnly = false) => {
  const inFlight = featuredOnly ? inFlightFeaturedPromise : inFlightPromise;
  
  if (!inFlight) {
    const promise = requestHotels(featuredOnly)
      .then((data) => {
        if (featuredOnly) {
          cachedFeaturedHotels = data;
          cachedFeaturedError = null;
          cachedFeaturedTimestamp = Date.now();
        } else {
          cachedHotels = data;
          cachedError = null;
          cachedTimestamp = Date.now();
        }
        return data;
      })
      .catch((error) => {
        const errorMsg = formatErrorMessage(error);
        if (featuredOnly) {
          cachedFeaturedHotels = [];
          cachedFeaturedError = errorMsg;
          cachedFeaturedTimestamp = Date.now();
        } else {
          cachedHotels = [];
          cachedError = errorMsg;
          cachedTimestamp = Date.now();
        }
        throw errorMsg;
      })
      .finally(() => {
        if (featuredOnly) {
          inFlightFeaturedPromise = null;
        } else {
          inFlightPromise = null;
        }
      });
    
    if (featuredOnly) {
      inFlightFeaturedPromise = promise;
    } else {
      inFlightPromise = promise;
    }
    
    return promise;
  }

  return inFlight;
};

export const useHotelsData = (featuredOnly = false) => {
  const catalog = usePublicCatalogData();
  const contextRaw = featuredOnly
    ? (catalog?.featuredHotels != null ? catalog.featuredHotels : null)
    : (catalog?.hotels != null ? catalog.hotels : null);
  const contextHotels = useMemo(
    () => (Array.isArray(contextRaw) ? contextRaw.map(transformHotelRecord) : null),
    [contextRaw]
  );
  const cached = featuredOnly ? cachedFeaturedHotels : cachedHotels;
  const cachedErr = featuredOnly ? cachedFeaturedError : cachedError;
  const cachedTime = featuredOnly ? cachedFeaturedTimestamp : cachedTimestamp;
  
  const [state, setState] = useState(() => ({
    hotels: contextHotels || cached || [],
    loading: contextHotels == null && !cached && !cachedErr,
    error: cachedErr
  }));

  useEffect(() => {
    let mounted = true;

    if (contextHotels != null) {
      setState({
        hotels: contextHotels,
        loading: false,
        error: null
      });
      return () => {
        mounted = false;
      };
    }

    if (cached || cachedErr) {
      setState({
        hotels: cached || [],
        loading: false,
        error: cachedErr
      });
      const isStale = cachedTime > 0 && Date.now() - cachedTime > CACHE_TTL_MS;
      if (isStale) {
        loadHotels(featuredOnly)
          .then((data) => {
            if (!mounted) return;
            setState({
              hotels: data,
              loading: false,
              error: null
            });
          })
          .catch((errorMessage) => {
            if (!mounted) return;
            setState((prev) => ({
              ...prev,
              error: formatErrorMessage(errorMessage)
            }));
          });
      }
      return () => {
        mounted = false;
      };
    }

    setState((prev) => ({ ...prev, loading: true }));

    loadHotels(featuredOnly)
      .then((data) => {
        if (!mounted) return;
        setState({
          hotels: data,
          loading: false,
          error: null
        });
      })
      .catch((errorMessage) => {
        if (!mounted) return;
        setState({
          hotels: [],
          loading: false,
          error: formatErrorMessage(errorMessage)
        });
      });

    return () => {
      mounted = false;
    };
  }, [featuredOnly, cached, cachedErr, cachedTime, contextHotels]);

  return state;
};

export const resetHotelsCache = () => {
  cachedHotels = null;
  cachedError = null;
};

export const getHotelFallbacks = () => ({
  fallbackImages: [],
  fallbackFeatures
});

export default useHotelsData;

